import Color from 'colorjs.io';
import {Action} from './action';
import Decimal from './break_infinity';
import {
  makeApplyStatusEffectToPlayerAction,
  makeChooseRandomAction,
  makeTakeDamageAction,
  stepAction,
  trackPlayerAction,
  makeTweakHeadingAction,
  makeSpawnAction,
  makeAddResourceAction,
  makeIntervalAction,
  makeOnceAction,
  makeRepeatAction,
  makeAvoidPlayerAction,
  makeTrackPlayerAction,
  makeAtVisionRangeAction,
} from './actions';
import {Creature} from './creature';
import {makeBurnTemplate, makeWitherTemplate} from './status-effect';
import {StatusEffect} from './status-effect';
import {Gettable} from './types/runner-idle';
import {ri, RunnerIdle} from './runner-idle';
import {Entity} from './actor';
import {Color, Color} from 'p5';

/**
 * Deer are big, landbound Creatures.
 * Fairly fast, fairly weak, nonaggressive.
 */
export const deerTemplate = {
  name: 'deer',
  fillColor: [255, 0, 0] as [number, number, number],
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(3)},
  },
  loot: new Map([['meat', 5]]),
  makeActions(this: Creature): Action[] {
    return [stepAction, makeTweakHeadingAction(Math.PI / 8)];
  },
};

/**
 * Slime are the first hostile enemy you encounter.
 * Slow and weak, slime gradually heal and can rarely reproduce (not implemented right now).
 *
 */
export const slimeTemplate = {
  name: 'slime',
  imageString: 'slime',
  imageHues: [new Color('#ff0000'), new Color('#00ff00'), new Color('#0000ff')],
  interactionRange: new Decimal(1),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(1)},
  },
  resources: {
    health: {value: new Decimal(4), capacity: new Decimal(4)},
  },
  loot: new Map([['gel' as Gettable, 1]]),
  makeActions(this: Creature) {
    return [
      makeIntervalAction(
        makeAddResourceAction('health', new Decimal(1)),
        new Decimal(10)
      ),
      stepAction,
      makeTrackPlayerAction(new Decimal(10), new Decimal(1)),
      makeTweakHeadingAction(Math.PI / 8),
    ];
  },
};

/**
 * Slime Boss is the first boss-style enemy you encounter.
 * Big and strong, Slime Boss splits into more slime each time you kill him!
 *
 */
export const slimeBossTemplate = {
  name: 'slimeBoss',
  imageString: 'slime',
  bodySize: new Decimal(16),
  interactionRange: new Decimal(16),
  stats: {
    strength: {value: new Decimal(2)},
    speed: {value: new Decimal(4)},
  },
  resources: {
    health: {value: new Decimal(64), capacity: new Decimal(64)},
  },
  loot: new Map([['gel' as Gettable, 1]]),
  makeActions(this: Creature) {
    return [
      makeIntervalAction(
        makeAddResourceAction('health', new Decimal(1)),
        new Decimal(1)
      ),
      stepAction,
      // targetPlayerAction,
      makeTweakHeadingAction(Math.PI / 8),
      makeSpawnAction({
        spawnTemplate: slimeTemplate,
        spawnInterval: new Decimal(60),
      }),
    ];
  },
  makeDeathActions(this: Creature) {
    return [
      makeRepeatAction(
        makeSpawnAction({
          spawnRange: new Decimal(5),
          spawnTemplate: {
            ...this.breed,
            interactionRange: this.interactionRange.div(2).floor(),
            bodySize: this.bodySize.div(2).floor(),
            resources: {
              health: {
                capacity: this.resources.health?.capacity?.div(2).floor(),
                value: this.resources.health?.capacity?.div(2).floor(),
              },
            },
          },
        }),
        2
      ),
    ];
  },
};

/**
 * Squirrels are small creatures.
 * They avoid the player,
 * and are quite quick but jittery.
 */
export const squirrelTemplate: Partial<Creature> = {
  name: 'squirrel',
  imageString: 'slime',
  bodySize: new Decimal(0.2),
  interactionRange: new Decimal(0.5),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(9)},
  },
  resources: {
    health: {value: new Decimal(1)},
  },
  loot: new Map([['meat' as Gettable, 1]]),
  makeActions(this: Creature) {
    return [
      stepAction,
      makeAvoidPlayerAction(new Decimal(50), new Decimal(1)),
      makeTweakHeadingAction(Math.PI / 8),
    ];
  },
};

/**
 * Ducks are small creatures.
 * They avoid the player,
 * and are quite quick but jittery.
 */
export const duckTemplate: Partial<Creature> = {
  name: 'duck',
  imageString: 'slime',
  bodySize: new Decimal(0.2),
  interactionRange: new Decimal(0.5),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(9)},
  },
  resources: {
    health: {value: new Decimal(1)},
  },
  loot: new Map([['meat' as Gettable, 1]]),
  tags: new Set(['waterwalking']),
  makeActions(this: Creature) {
    return [
      stepAction,
      makeAvoidPlayerAction(new Decimal(50), new Decimal(1)),
      makeTweakHeadingAction(Math.PI / 8),
    ];
  },
};

/**
 * Shadows follow the player, but stay just out of reach.
 * They deal Strength damage on contact!!! not implemented
 */
export const shadowTemplate: Partial<Creature> = {
  name: 'shadow',
  imageString: 'slime',
  bodySize: new Decimal(0.2),
  interactionRange: new Decimal(0.5),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(9)},
  },
  resources: {
    health: {value: new Decimal(1)},
  },
  tags: new Set(['waterwalking']),
  makeActions(this: Creature) {
    return [
      stepAction,
      makeAtVisionRangeAction(),
      makeTweakHeadingAction(Math.PI / 2),
    ];
  },
};

/**
 * Goblins are small but dangerous in numbers.
 */
export const goblinTemplate: Partial<Creature> = {
  name: 'goblin',
  imageString: 'slime',
  imageHues: [new Color('#002200'), new Color('#ff0000'), new Color('#002200')],
  bodySize: new Decimal(0.5),
  interactionRange: new Decimal(1),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(1)},
  },
  resources: {
    health: {value: new Decimal(10), capacity: new Decimal(10)},
  },
  loot: new Map([['meat' as Gettable, 1]]),
  makeActions(this: Creature) {
    return [
      stepAction,
      makeTweakHeadingAction(Math.PI / 8),
      makeTrackPlayerAction(new Decimal(100), new Decimal(1)),
      makeTweakHeadingAction(Math.PI / 16),
    ];
  },
};

export const fireballTemplate = {
  name: `fireball`,
  displayName: `Fireball`,
  imageString: 'slime',
  bodySize: new Decimal(1),
  imageHues: [new Color('#ff0000'), new Color('#ff0000'), new Color('#ff8800')],
  fillColor: [255, 0, 0] as [number, number, number],
  tags: new Set(['orb', 'flying']),
  interactionRange: new Decimal(3),
  stats: {
    strength: {value: new Decimal(5)},
    speed: {value: new Decimal(2)},
  },
  resources: {
    health: {value: new Decimal(200)},
  },
  onInteract(this: Creature, _distance: Decimal, entity: Entity) {
    if (entity instanceof RunnerIdle) {
      entity.addStatusEffect(StatusEffect.create(makeBurnTemplate()));
    }
  },
  makeActions(this: Creature) {
    return [
      makeOnceAction('chooseHeading', trackPlayerAction),
      stepAction,
      makeTakeDamageAction(new Decimal(10)),
    ];
  },
};

/**
 * Goblins are small but dangerous in numbers.
 */
export const goblinMageTemplate: Partial<Creature> = {
  name: 'goblinMage',
  imageString: 'slime',
  imageHues: [
    new Color('#002200'),
    new Color('#ff0000'),
    new Color('#002200'),
  ] as const,
  bodySize: new Decimal(0.5),
  interactionRange: new Decimal(1),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(1)},
  },
  resources: {
    health: {value: new Decimal(10), capacity: new Decimal(10)},
  },
  loot: new Map([['meat' as Gettable, 1]]),
  makeActions(this: Creature) {
    return [
      stepAction,
      trackPlayerAction,
      makeAvoidPlayerAction(new Decimal(10), new Decimal(1)),
      makeSpawnAction({
        spawnTemplate: fireballTemplate,
        spawnInterval: new Decimal(10),
      }),
      makeTweakHeadingAction(Math.PI / 16),
    ];
  },
};

/**
 * Grue follow the player around, staying just outside their vision range.
 * Unlike Shadows, they are a corporeal threat in combat,
 * and are devastating if you lose sight of them and are ambushed.
 */
export const grueTemplate: Partial<Creature> = {
  name: 'grue',
  imageString: 'slime',
  imageHues: [new Color('#000000'), new Color('#000000'), new Color('#000000')],
  bodySize: new Decimal(0.5),
  interactionRange: new Decimal(1),
  stats: {
    strength: {value: new Decimal(10)},
    speed: {value: new Decimal(10)},
  },
  resources: {
    health: {value: new Decimal(10), capacity: new Decimal(10)},
  },
  loot: new Map([['meat' as Gettable, 10]]),
  makeActions(this: Creature) {
    return [stepAction, makeAtVisionRangeAction()];
  },
};

/**
 * Mimics pretend to be chests or others pieces of furniture,
 * only to attack as soon as you open them up.
 * Slow but deadly, if you fell for it you're likely dead.
 */
export const mimicTemplate = {
  name: 'mimic',
  imageString: 'slime',
  imageHues: [new Color('#663300'), new Color('#994400'), new Color('#772200')],
  interactionRange: new Decimal(1),
  stats: {
    strength: {value: new Decimal(5)},
    speed: {value: new Decimal(0.5)},
  },
  resources: {
    health: {value: new Decimal(10), capacity: new Decimal(10)},
  },
  makeActions(this: Creature) {
    return [
      stepAction,
      makeIntervalAction(
        makeAddResourceAction('health', new Decimal(10)),
        new Decimal(5)
      ),
      makeTrackPlayerAction(new Decimal(10), new Decimal(1)),
      makeTweakHeadingAction(Math.PI / 8),
    ];
  },
};

export const makeStatusOrbTemplate = (
  statusEffect: StatusEffect,
  overrides = {}
) => ({
  name: `${statusEffect.name}Orb`,
  displayName: `${statusEffect.displayName} Orb`,
  imageString: 'slime',
  bodySize: new Decimal(5),
  tags: new Set(['orb', 'flying']),
  interactionRange: new Decimal(20),
  stats: {
    strength: {value: new Decimal(5)},
    speed: {value: new Decimal(5)},
  },
  resources: {
    health: {value: new Decimal(300)},
  },
  onInteract(this: Creature, _distance: Decimal, entity: Entity) {
    if (entity instanceof RunnerIdle) {
      entity.addStatusEffect(statusEffect);
    }
  },
  makeActions(this: Creature) {
    return [
      stepAction,
      makeOnceAction('chooseHeading', makeTweakHeadingAction(Math.PI)),
      makeTakeDamageAction(new Decimal(1)),
    ];
  },
  ...overrides,
});

export const witherStaminaOrbTemplate = makeStatusOrbTemplate(
  StatusEffect.create(makeWitherTemplate('stamina')),
  {
    imageHues: [
      new Color('#00ff00'),
      new Color('#00ff00'),
      new Color('#00ff00'),
    ],
    fillColor: [0, 255, 0] as [number, number, number],
  }
);
export const witherHealthOrbTemplate = makeStatusOrbTemplate(
  StatusEffect.create(makeWitherTemplate('health')),
  {
    imageHues: [
      new Color('#ff0000'),
      new Color('#ff0000'),
      new Color('#ff0000'),
    ],
    fillColor: [255, 0, 0] as [number, number, number],
  }
);

export const witherBossTemplate = {
  name: `witherBoss`,
  displayName: 'Wither Boss',
  imageString: 'slime',
  bodySize: new Decimal(10),
  interactionRange: new Decimal(10),
  stats: {
    strength: {value: new Decimal(1)},
    speed: {value: new Decimal(1)},
  },
  resources: {
    health: {value: new Decimal(1)},
  },
  makeActions(this: Creature) {
    return [
      stepAction,
      makeTweakHeadingAction(Math.PI),
      makeChooseRandomAction([
        makeSpawnAction({
          spawnTemplate: witherStaminaOrbTemplate,
          spawnInterval: new Decimal(10),
          spawnMaxChildren: new Decimal(10),
        }),
        makeSpawnAction({
          spawnTemplate: {
            ...witherHealthOrbTemplate,
            makeActions() {
              return [
                ...witherHealthOrbTemplate.makeActions.bind(this)(),
                makeTrackPlayerAction(new Decimal(1000), new Decimal(1)),
              ];
            },
          },
          spawnInterval: new Decimal(60),
          spawnMaxChildren: new Decimal(10),
        }),
      ]),
    ];
  },
};

interface CreatureTemplate {
  name: string;
  imageString: string;
  stats: Partial<Creature['stats']>;
  resources: Partial<Creature['resources']>;
  loot: Creature['loot'];
  makeActions(this: Creature): Action[];
}

/**
 * Zombies
 */
export const makeZombieTemplate: (
  baseTemplate: CreatureTemplate
) => CreatureTemplate = (baseTemplate: CreatureTemplate) => ({
  ...baseTemplate,
  name: `zombie ${baseTemplate.name}`,
  imageHues: [new Color('#4f6936'), new Color('#557239'), new Color('#466d43')],
  stats: {
    ...baseTemplate.stats,
    speed: {
      ...baseTemplate.stats.speed,
      value: baseTemplate.stats.speed?.value?.div(5) ?? new Decimal(0),
    },
  },
  makeActions() {
    return [...baseTemplate.makeActions.bind(this)(), trackPlayerAction];
  },
});

export const CreatureTemplates = new Map([
  ['deer', deerTemplate],
  ['squirrel', squirrelTemplate],
  ['duck', duckTemplate],
  ['shadow', shadowTemplate],
  ['goblin', goblinTemplate],
  ['goblinMage', goblinMageTemplate],
  ['fireball', fireballTemplate],
  ['grue', grueTemplate],
  ['slime', slimeTemplate],
  ['slimeBoss', slimeBossTemplate],
  ['witherStaminaOrb', witherStaminaOrbTemplate],
  ['witherHealthOrb', witherHealthOrbTemplate],
  ['witherBoss', witherBossTemplate],
]);
